// The AE table's columns (GRO-360, reworked 20 Oct after Vikram's review).
//
// Every column has a plain header and is about the AE's deal or the people around it: Account,
// Stage, Amount, Close date, Next step, First meeting, Next meeting, AE touch, SDR touch, SDR owner, Usage 30d.
// Stage, Amount, Close date and Next step all read the one lead deal (`leadDeal`), so a row never
// mixes two deals; the others show as "+N more open" under the stage. The widths add up to 934px,
// under the 982px a 1024px window leaves, so the table fits there without scrolling (Account and
// Next step take the slack). With scores visible, Fit and Engagement (the board's own score cells) follow
// Amount (Fit 44px, Engagement 78px), paid for by a narrower Account, Stage, Next step, SDR owner, Usage, Close date and the AE touch column (982px). Stage turns
// warn-coloured on any stage 1.
// Every row is the same height: each cell is one line or two, clamped, with the full text on hover.
// Every column sorts; the sections (`aeSections.ts`) stay above any sort.

import type { ReactNode } from 'react';
import type { BookColumn, CellContext } from '../book/columns';
import type { RankedAccount } from '../book/derived';
import { AccountHover } from '../book/AccountHover';
import { boardV2Columns, compactScoreColumns } from '../book/boardV2Columns';
import { employeesLabel } from '../book/boardCells';
import { HoverCard } from '../components/HoverCard';
import { openOpportunities } from '../sidebar/panelFacts';
import { companyUrl, displayAccountName, firstName, formatMoney } from '../lib/format';
import { isStageOne, leadDeal, nextMeetingDay } from './aeSections';
import { daysFrom, leadDealAmount, leadDealCloseDay, leadDealFirstMeetingDay, relativeDay, shortDay } from './aeFacts';

function Dash() {
  return <span className="dash">—</span>;
}

// The payload's own day, as YYYY-MM-DD, from the context's Date.
function todayOf(context: CellContext): string {
  return context.today.toISOString().slice(0, 10);
}

// Name, then a muted line of size and industry. One line each, so every row stays the same
// height; the card on hover holds the full name, size, industry and every flag.
function AccountCell({ row, context }: { row: RankedAccount; context: CellContext }) {
  const href = companyUrl(row.domain, row.website);
  const employees = employeesLabel(row);
  const industry = row.industry || row.clay?.engine?.sector || row.clay?.engine?.industry || null;
  const secondary = [employees, industry].filter(Boolean).join(' · ');
  const displayName = displayAccountName(row.account);
  return (
    <AccountHover row={row} name={row.account} today={context.today}>
      <div className="aeacct">
        {href
          ? <a className="dname aeline" href={href} target="_blank" rel="noopener">{displayName}</a>
          : <span className="dname aeline">{displayName}</span>}
        {secondary ? <div className="aesub aeline">{secondary}</div> : null}
      </div>
    </AccountHover>
  );
}

// The lead deal's full stage name, and how many more are open.
function StageCell({ row }: { row: RankedAccount }) {
  const deal = leadDeal(row);
  if (!deal) return <Dash />;
  const more = openOpportunities(row).length - 1;
  return (
    <div title={deal.name ?? undefined}>
      <div className={(more > 0 ? 'aeline' : 'aeclamp') + (isStageOne(deal.stage) ? ' warn' : '')}>{deal.stage ?? 'Open'}</div>
      {more > 0 ? <div className="aesub">+{more} more open</div> : null}
    </div>
  );
}

function AmountCell({ row }: { row: RankedAccount }) {
  const amount = leadDealAmount(row);
  return amount == null ? <Dash /> : <>{formatMoney(amount)}</>;
}

// Two lines at most; the whole of it in the board's hover card.
function NextStepCell({ row }: { row: RankedAccount }) {
  const nextStep = leadDeal(row)?.next_step;
  if (!nextStep) return <Dash />;
  return (
    <HoverCard card={() => <div className="aenextcard">{nextStep}</div>} label={`Next step: ${nextStep}`} className="aenexttrigger">
      <span className="aeclamp">{nextStep}</span>
    </HoverCard>
  );
}

// A day ahead: the date, and how far off it is. Past dates turn the second line amber.
function UpcomingDay({ day, context }: { day: string; context: CellContext }) {
  const today = todayOf(context);
  const overdue = daysFrom(day, today) < 0;
  return (
    <div title={day}>
      <div className="aeline">{shortDay(day, today)}</div>
      <div className={'aesub aeline' + (overdue ? ' warn' : '')}>{overdue ? `${relativeDay(day, today).replace(' ago', '')} late` : relativeDay(day, today)}</div>
    </div>
  );
}

// The first meeting: the date, then how long ago (or how far off, if it is still to come). Never amber.
function FirstMeetingDay({ day, context }: { day: string; context: CellContext }) {
  const today = todayOf(context);
  return (
    <div title={day}>
      <div className="aeline">{shortDay(day, today)}</div>
      <div className="aesub aeline">{relativeDay(day, today)}</div>
    </div>
  );
}

// A day behind: how long ago, and the date. Stale turns amber.
function PastDay({ day, context }: { day: string; context: CellContext }) {
  const today = todayOf(context);
  const isStale = -daysFrom(day, today) >= context.staleDays;
  return (
    <div title={day}>
      <div className={'aeline' + (isStale ? ' warn' : '')}>{relativeDay(day, today)}</div>
      <div className="aesub aeline">{shortDay(day, today)}</div>
    </div>
  );
}

function dayColumn(
  key: string,
  label: string,
  hint: string,
  width: string,
  day: (row: RankedAccount) => string | null,
  render: (day: string, context: CellContext) => ReactNode,
  emptyText: string | null = null,
): BookColumn {
  return {
    key,
    label,
    hint,
    width,
    sortValue: row => day(row) ?? null,
    accessor: row => day(row) ?? '',
    cell: (row, context) => {
      const value = day(row);
      if (value) return render(value, context);
      return emptyText ? <span className="zero">{emptyText}</span> : <Dash />;
    },
  };
}

// The columns, in order. Usage is the board's own cell, so it reads the same on both tables.
export function aeColumns(includeScores = false): BookColumn[] {
  const usage = boardV2Columns().find(column => column.key === 'credits_30')!;
  const narrow = (wide: number, scored: number) => `${includeScores ? scored : wide}px`;

  return [
    {
      key: 'account', label: 'Account', width: narrow(150, 138),
      hint: 'Employees and industry under the name.',
      sortValue: row => row.account,
      accessor: row => row.account,
      cell: (row, context) => <AccountCell row={row} context={context} />,
    },
    {
      key: 'deal_stage', label: 'Stage', width: narrow(104, 92),
      hint: 'The stage of the furthest-along open opportunity, and how many more are open.',
      sortValue: row => leadDeal(row)?.stage ?? null,
      accessor: row => leadDeal(row)?.stage ?? '',
      cell: row => <StageCell row={row} />,
    },
    {
      key: 'amount', label: 'Amount', width: '78px', numeric: true,
      hint: 'The lead opportunity’s Salesforce Amount, or its qualified pipeline where no Amount is set.',
      sortValue: row => leadDealAmount(row),
      accessor: row => String(leadDealAmount(row) ?? ''),
      cell: row => <AmountCell row={row} />,
    },
    ...(includeScores ? compactScoreColumns() : []),
    dayColumn('close_date', 'Close date', 'The lead opportunity’s close date.', '68px',
      row => leadDealCloseDay(row), (day, context) => <UpcomingDay day={day} context={context} />),
    {
      key: 'next_step', label: 'Next step', width: narrow(100, 78), flex: true,
      hint: 'The next step written on the lead opportunity in Salesforce. Hover for all of it.',
      sortValue: row => leadDeal(row)?.next_step ?? null,
      accessor: row => leadDeal(row)?.next_step ?? '',
      cell: row => <NextStepCell row={row} />,
    },
    dayColumn('first_meeting', 'First meeting', 'The first meeting on the lead opportunity, from Salesforce.', '70px',
      row => leadDealFirstMeetingDay(row), (day, context) => <FirstMeetingDay day={day} context={context} />),
    dayColumn('next_meeting', 'Next meeting', 'The next booked meeting, yours or the SDR’s.', '70px',
      row => nextMeetingDay(row, row.book_today ?? ''), (day, context) => <UpcomingDay day={day} context={context} />),
    dayColumn('last_touch_ae', 'AE touch', 'Your last logged activity on the account.', '64px',
      row => row.last_touch_by_ae ?? null, (day, context) => <PastDay day={day} context={context} />, 'never'),
    dayColumn('last_touch_sdr', 'SDR touch', 'The SDR’s last outbound touch on the account.', '68px',
      row => row.last_touch, (day, context) => <PastDay day={day} context={context} />, 'never'),
    {
      key: 'sdr_owner', label: 'SDR owner', width: '62px',
      hint: 'The SDR (strategist) who owns outbound on the account.',
      sortValue: row => (row.sdr_owner ? firstName(row.sdr_owner) : null),
      accessor: row => row.sdr_owner ?? '',
      cell: row => (row.sdr_owner
        ? <div className="aeline" title={row.sdr_owner}>{firstName(row.sdr_owner)}</div>
        : <Dash />),
    },
    { ...usage, label: 'Usage 30d', width: narrow(88, 72) },
  ];
}
